import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleFeaturesClick = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('features');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    setMobileMenuOpen(false);
  };

  const handlePrivacyClick = () => {
    navigate('/privacy');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
    setMobileMenuOpen(false);
  };

  const handlePricingClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-20 sm:h-24">
          <button onClick={() => navigate('/')} className="flex items-center gap-4 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/151e34d9a5807cd608014213251345d2/8df0e05ed936c289fbef98f2071860df.png"
              alt="HushLegal"
              className="h-12 sm:h-16 w-auto"
            />
            <span className={`text-[24px] sm:text-[32px] font-black tracking-tight transition-colors duration-300 ${
              scrolled ? 'text-gray-900' : 'text-white'
            }`}>
              HushLegal
            </span>
          </button>

          <div className="hidden lg:flex items-center gap-10">
            <button
              onClick={handleFeaturesClick}
              className={`text-[15px] transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-600 hover:text-[#8B4513]' : 'text-white/90 hover:text-white'
              }`}
            >
              Features
            </button>
            <button
              onClick={handlePrivacyClick}
              className={`text-[15px] transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-600 hover:text-[#8B4513]' : 'text-white/90 hover:text-white'
              }`}
            >
              Privacy
            </button>
            <button
              onClick={handlePricingClick}
              className={`text-[15px] transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-600 hover:text-[#8B4513]' : 'text-white/90 hover:text-white'
              }`}
            >
              Pricing
            </button>
            <button
              onClick={() => scrollToSection('final-cta')}
              className="bg-[#d4af37] text-black text-[14px] font-bold px-7 py-3 rounded-full shadow-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] hover:translate-y-[-2px] transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Claim Founders Spot
            </button>
          </div>

          <button
            className="lg:hidden flex flex-col items-center justify-center w-10 h-10 gap-[6px] cursor-pointer relative z-[60]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span
              className={`block w-6 h-[2px] rounded-full transition-all duration-300 ${
                scrolled ? 'bg-gray-900' : 'bg-white'
              } ${mobileMenuOpen ? 'rotate-45 translate-y-[8px] !bg-gray-900' : ''}`}
            ></span>
            <span
              className={`block w-6 h-[2px] rounded-full transition-all duration-300 ${
                scrolled ? 'bg-gray-900' : 'bg-white'
              } ${mobileMenuOpen ? 'opacity-0 scale-0' : ''}`}
            ></span>
            <span
              className={`block w-6 h-[2px] rounded-full transition-all duration-300 ${
                scrolled ? 'bg-gray-900' : 'bg-white'
              } ${mobileMenuOpen ? '-rotate-45 -translate-y-[8px] !bg-gray-900' : ''}`}
            ></span>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[55] transition-opacity duration-300 lg:hidden ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      ></div>

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[280px] bg-white border-l border-gray-200 z-[56] transition-transform duration-300 ease-out lg:hidden ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col pt-24 px-8 gap-2">
          <button
            onClick={handleFeaturesClick}
            className="text-gray-600 text-[18px] font-medium py-4 text-left hover:text-[#8B4513] transition-colors duration-200 border-b border-gray-200 cursor-pointer"
          >
            Features
          </button>
          <button
            onClick={handlePrivacyClick}
            className="text-gray-600 text-[18px] font-medium py-4 text-left hover:text-[#8B4513] transition-colors duration-200 border-b border-gray-200 cursor-pointer"
          >
            Privacy
          </button>
          <button
            onClick={handlePricingClick}
            className="text-gray-600 text-[18px] font-medium py-4 text-left hover:text-[#8B4513] transition-colors duration-200 border-b border-gray-200 cursor-pointer"
          >
            Pricing
          </button>
          <button
            onClick={() => scrollToSection('final-cta')}
            className="bg-[#d4af37] text-black text-[16px] font-bold px-7 py-4 rounded-full shadow-lg mt-6 whitespace-nowrap cursor-pointer hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] transition-all duration-200"
          >
            Claim Founders Spot
          </button>
        </div>
      </div>
    </nav>
  );
}
